'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useSearchParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarIcon,
  EditIcon,
  EyeIcon,
  MoreHorizontalIcon,
  DatabaseIcon,
  HardDriveIcon,
  PlayIcon,
  PlusIcon,
  SquareMenuIcon,
  Trash2Icon,
  ClockIcon,
  ServerIcon,
  ArrowLeftIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { DeleteConfirmationDialog, LoadingState, SortableHeader } from '@/components/partials'
import { BackupScheduleFilters } from '@/components/partials/backup-schedules'
import {
  useBackupSchedules,
  useDeleteBackupSchedule,
  useBulkDeleteBackupSchedules,
  useRunBackupSchedule
} from '@/hooks'
import { useBackupSchedulesStore } from '@/store'
import { BackupsFilterParams } from '@/types/backup'
import { SortEnum } from '@/enums'
import { formatDateTime } from '@/lib/utils'
import { BulkDeleteResponse } from '@/types/general'

const BackupScheduleList = () => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()

  const [deleteBackupId, setDeleteBackupId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false)

  const selectedBackupIds = useBackupSchedulesStore(state => state.selectedBackupIds)
  const toggleBackupSelection = useBackupSchedulesStore(state => state.toggleBackupSelection)
  const selectAllBackups = useBackupSchedulesStore(state => state.selectAllBackups)
  const deselectAllBackups = useBackupSchedulesStore(state => state.deselectAllBackups)
  const isBackupSelected = useBackupSchedulesStore(state => state.isBackupSelected)

  const filters = useMemo<BackupsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())
    const createdUsersParam = params.get('createdUsers')
    const updatedUsersParam = params.get('updatedUsers')
    const enabled = params.get('enabled')
    const includeDatabase = params.get('includeDatabase')
    const includeStorage = params.get('includeStorage')

    return {
      page: parseInt(params.get('page') || '1', 10),
      size: parseInt(params.get('size') || '20', 10),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || undefined,
      enabled: enabled === null ? undefined : enabled === 'true',
      includeDatabase: includeDatabase === null ? undefined : includeDatabase === 'true',
      includeStorage: includeStorage === null ? undefined : includeStorage === 'true',
      createdUsers: createdUsersParam ? createdUsersParam.split(',').filter(Boolean) : undefined,
      updatedUsers: updatedUsersParam ? updatedUsersParam.split(',').filter(Boolean) : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])

  const { data, isLoading, error, refetch, isFetching } = useBackupSchedules(filters)
  const { mutate: deleteBackupMutate, isPending: isDeleting } = useDeleteBackupSchedule()
  const { mutate: bulkDeleteMutate, isPending: isBulkDeleting } = useBulkDeleteBackupSchedules()
  const { mutate: runMutate, isPending: isRunning } = useRunBackupSchedule()

  const totalPages = data?.pages ?? 1
  const currentPage = data?.page ?? filters.page ?? 1
  const pageSize = data?.size ?? filters.size ?? 20

  const updateUrlParams = (newFilters: Partial<BackupsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<BackupsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handleSelectAll = () => {
    if (data && data.items.length > 0) {
      if (selectedBackupIds.length === data.items.length) {
        deselectAllBackups()
      } else {
        selectAllBackups(data.items)
      }
    }
  }

  const confirmDeleteBackup = () => {
    if (!deleteBackupId) {
      return
    }

    deleteBackupMutate(deleteBackupId, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['backup-schedules'] })
        toast.success(t('deleted_successfully'))
        setDeleteBackupId(null)
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message ?? t('error_deleting'))
        setDeleteBackupId(null)
      }
    })
  }

  const confirmBulkDeleteBackups = () => {
    if (selectedBackupIds.length === 0) {
      return
    }

    bulkDeleteMutate(selectedBackupIds, {
      onSuccess: async (_response: BulkDeleteResponse) => {
        await queryClient.invalidateQueries({ queryKey: ['backup-schedules'] })
        setBulkDeleteConfirmOpen(false)
      },
      onError: (error: any) => {
        if (error.response?.status === 422) {
          toast.error(error.response.data?.items?.ids || t('error_deleting'))
        } else {
          toast.error(t('error_deleting'))
        }
        setBulkDeleteConfirmOpen(false)
      }
    })
  }

  const handleRunNow = (id: string) => {
    runMutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['backup-schedules'] })
        toast.success(t('backup_run_success'))
      },
      onError: () => {
        toast.error(t('error_updating'))
      }
    })
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            router.push(`/backups/schedules/${id}`)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('backup_schedule_details')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            router.push(`/backups/schedules/${id}/edit`)
          }}>
          <EditIcon className="h-4 w-4" />
          {t('edit_backup_schedule')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleRunNow(id)
          }}
          disabled={isRunning}>
          <PlayIcon className="h-4 w-4" />
          {t('run_now')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            setDeleteBackupId(id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between sm:gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ServerIcon className="h-5 w-5" />
                {t('backup_schedules')}
              </CardTitle>
              <CardDescription>{t('backup_schedules_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {selectedBackupIds.length > 0 && (
                <Button variant="destructive" onClick={() => setBulkDeleteConfirmOpen(true)} disabled={isBulkDeleting}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedBackupIds.length})</span>
                  </div>
                </Button>
              )}
              <Button variant="outline" onClick={() => router.push('/backups')}>
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('backups')}</span>
              </Button>
              <Button onClick={() => router.push('/backups/schedules/create')}>
                <PlusIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('create_backup_schedule')}</span>
              </Button>
              <BackupScheduleFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={() => router.push('/backups/schedules')}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {data && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={data.items.length > 0 && selectedBackupIds.length === data.items.length}
                              onCheckedChange={handleSelectAll}
                              onClick={e => e.stopPropagation()}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-60">{t('days')}</TableHead>
                        <TableHead className="min-w-24">
                          <SortableHeader
                            sortBy="hour"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <ClockIcon className="h-4 w-4" />
                            {t('time')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-24">{t('keep_count')}</TableHead>
                        <TableHead className="min-w-24 text-center">
                          <DatabaseIcon className="mr-1 inline h-4 w-4" />
                          {t('database')}
                        </TableHead>
                        <TableHead className="min-w-24 text-center">
                          <HardDriveIcon className="mr-1 inline h-4 w-4" />
                          {t('storage')}
                        </TableHead>
                        <TableHead className="min-w-24">
                          <SortableHeader
                            sortBy="enabled"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('enabled')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-24 text-center">{t('backups_count')}</TableHead>
                        <TableHead className="min-w-40">
                          <SortableHeader
                            sortBy="lastRunAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('last_run_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40">
                          <SortableHeader
                            sortBy="nextRunAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('next_run_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.items.map((backup, index) => (
                          <ContextMenu key={backup.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-background' : 'bg-muted/25'}`}
                                onClick={() => router.push(`/backups/schedules/${backup.id}`)}>
                                <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                                  <Checkbox
                                    checked={isBackupSelected(backup.id)}
                                    onCheckedChange={() => toggleBackupSelection(backup.id)}
                                    onClick={e => e.stopPropagation()}
                                    className="cursor-pointer"
                                  />
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {(backup.days ?? []).map(day => (
                                      <Badge key={day} variant="secondary" className="text-xs">
                                        {t(`day_${day.toLowerCase()}` as any)}
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell className="pl-10">
                                  <span className="font-mono text-sm">
                                    {String(backup.hour).padStart(2, '0')}:{String(backup.minute).padStart(2, '0')}
                                  </span>
                                </TableCell>
                                <TableCell className="pr-10 text-right">{backup.keepCount}</TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={backup.includeDatabase ? 'success' : 'outline'} className="text-xs">
                                    {backup.includeDatabase ? t('yes') : t('no')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={backup.includeStorage ? 'success' : 'outline'} className="text-xs">
                                    {backup.includeStorage ? t('yes') : t('no')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="pr-10 text-center">
                                  <Badge variant={backup.enabled ? 'success' : 'secondary'}>
                                    {backup.enabled ? t('enabled') : t('disabled')}
                                  </Badge>
                                </TableCell>
                                <TableCell className="pr-10 text-right">
                                  <Badge variant="outline">{backup.backupsCount}</Badge>
                                </TableCell>
                                <TableCell className="text-muted-foreground pr-20 text-center text-sm">
                                  {backup.lastRunAt ? formatDateTime(backup.lastRunAt, locale) : '-'}
                                </TableCell>
                                <TableCell className="text-muted-foreground pr-20 text-center text-sm">
                                  {backup.nextRunAt ? formatDateTime(backup.nextRunAt, locale) : '-'}
                                </TableCell>
                                <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm">
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(backup.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(backup.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={data.total}
                  pageSize={pageSize}
                  onPageChangeAction={page => handleFiltersChange({ page })}
                  onPageSizeChangeAction={size => handleFiltersChange({ size, page: 1 })}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteBackupId}
        onCancel={() => setDeleteBackupId(null)}
        onConfirm={confirmDeleteBackup}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteBackups}
        isDeleting={isBulkDeleting}
      />
    </>
  )
}

export default BackupScheduleList
